import { useMemo, useState } from 'react';
import { Loader2, Plug, Save } from 'lucide-react';
import type {
  CustomEndpointKind,
  CustomEndpointPreset,
  CustomAIDeps,
  TestConnectionResult,
} from './types';

interface Props {
  deps: CustomAIDeps;
  presets: ReadonlyArray<CustomEndpointPreset>;
  onCreated: () => void;
  onCancel: () => void;
}

const BASE_URL_HINT =
  'Solo HTTPS pubblico. Localhost/RFC1918 bloccati: esponi via reverse proxy (es. Nginx + Let’s Encrypt).';

export function EndpointWizard({ deps, presets, onCreated, onCancel }: Props): JSX.Element {
  const { api, basePath, notify, formatError } = deps;
  const [kind, setKind] = useState<CustomEndpointKind>('openai_compatible');
  const [name, setName] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [headersRaw, setHeadersRaw] = useState<string>('');
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const selectedPreset = useMemo(() => presets.find((p) => p.kind === kind) ?? null, [presets, kind]);

  function parseHeaders(): Record<string, string> | null {
    const raw = headersRaw.trim();
    if (!raw) return {};
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v !== 'string') return null;
        out[k] = v;
      }
      return out;
    } catch {
      return null;
    }
  }

  async function handleTest(): Promise<void> {
    const headers = parseHeaders();
    if (headers === null) {
      notify({ title: 'Errore', description: 'Header JSON non valido (oggetto string→string).', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post<TestConnectionResult>(
        `${basePath}/endpoints/test-connection`,
        { base_url: baseUrl, api_key: apiKey, default_headers: headers },
      );
      const data = res.data ?? null;
      setTestResult(data);
      if (data?.ok) {
        notify({ title: `Connessione OK · ${data.models_found} modelli rilevati (${data.latency_ms}ms)` });
      } else {
        notify({ title: 'Errore', description: `Connessione fallita: ${data?.error ?? 'errore sconosciuto'}`, variant: 'destructive' });
      }
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Test connessione fallito'), variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(): Promise<void> {
    const headers = parseHeaders();
    if (headers === null) {
      notify({ title: 'Errore', description: 'Header JSON non valido.', variant: 'destructive' });
      return;
    }
    if (!name.trim() || !baseUrl.trim() || !apiKey.trim()) {
      notify({ title: 'Errore', description: 'Nome, base_url e api_key sono obbligatori.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post(`${basePath}/endpoints`, {
        name: name.trim(),
        kind,
        base_url: baseUrl.trim(),
        api_key: apiKey,
        default_headers: headers,
      });
      notify({ title: `Endpoint "${name}" creato.` });
      onCreated();
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Creazione endpoint fallita'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div>
        <label className="mb-1 block text-xs font-medium uppercase text-zinc-500">Provider</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {presets.map((p) => (
            <button
              key={p.kind}
              type="button"
              onClick={() => {
                setKind(p.kind);
                if (!baseUrl) setBaseUrl(p.defaultBaseUrlHint);
              }}
              className={`rounded-md border p-2 text-left text-xs transition ${
                kind === p.kind
                  ? 'border-sky-500 bg-sky-50 dark:bg-sky-950'
                  : 'border-zinc-200 hover:border-zinc-300 dark:border-zinc-700'
              }`}
            >
              <div className="font-semibold">{p.label}</div>
              <div className="text-[10px] text-zinc-500 line-clamp-2">{p.description}</div>
            </button>
          ))}
        </div>
        {selectedPreset && (
          <a
            href={selectedPreset.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-block text-xs text-sky-600 hover:underline"
          >
            Documentazione {selectedPreset.label} →
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-zinc-500">Nome</label>
          <input
            type="text"
            className="w-full rounded border border-zinc-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            placeholder="es. Ollama Prod"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-zinc-500">Base URL</label>
          <input
            type="url"
            className="w-full rounded border border-zinc-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            placeholder={selectedPreset?.defaultBaseUrlHint ?? 'https://api.tuo-provider.com/v1'}
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
          <p className="mt-1 text-[10px] text-zinc-500">{BASE_URL_HINT}</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-zinc-500">API Key</label>
          <input
            type="password"
            autoComplete="off"
            className="w-full rounded border border-zinc-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            placeholder="sk-..."
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-zinc-500">
            Header HTTP extra (JSON, opzionale)
          </label>
          <textarea
            rows={2}
            className="w-full rounded border border-zinc-200 px-2 py-1.5 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-800"
            placeholder='{"X-Tenant-Id":"acme"}'
            value={headersRaw}
            onChange={(e) => setHeadersRaw(e.target.value)}
          />
        </div>
      </div>

      {testResult && (
        <div
          className={`rounded border px-3 py-2 text-xs ${
            testResult.ok
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-200'
              : 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-200'
          }`}
        >
          {testResult.ok ? (
            <>
              Connessione OK · HTTP {testResult.http_status} · {testResult.latency_ms}ms ·{' '}
              {testResult.models_found} modelli{' '}
              {testResult.sample_models && testResult.sample_models.length > 0 && (
                <span className="opacity-70">(es. {testResult.sample_models.slice(0, 3).join(', ')})</span>
              )}
            </>
          ) : (
            <>Errore: {testResult.error ?? 'sconosciuto'} (HTTP {testResult.http_status ?? '—'})</>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || !baseUrl || !apiKey}
          className="inline-flex items-center gap-1.5 rounded bg-zinc-100 px-3 py-1.5 text-xs font-medium hover:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:hover:bg-zinc-700"
        >
          {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
          Test connessione
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !name || !baseUrl || !apiKey}
          className="inline-flex items-center gap-1.5 rounded bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Salva endpoint
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          Annulla
        </button>
      </div>
    </div>
  );
}
